import { useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useBottomSheetKeyboard } from '../../../hooks/useBottomSheetKeyboard';
import { Button } from '@safetyhub/ui';

const SEARCH_BAR_HEIGHT = 44;

export interface SearchableSelectOption {
  id: string;
  name: string;
}

export interface SearchableSelectProps {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  disabled?: boolean;
  onAdd?: (name: string) => Promise<string>; // 새 항목 추가 → 생성된 id 반환
  sheetMode?: boolean; // true이면 드롭다운 대신 bottom sheet로 열기
}

export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  onAdd,
  sheetMode = false,
}: SearchableSelectProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({ left: 0, width: 0, maxHeight: 160 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sheetInputRef = useRef<HTMLInputElement>(null);

  const sheetStyle = useBottomSheetKeyboard(sheetOpen);

  // 드롭다운 위치 계산 (항상 아래로 열기)
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const GAP = 4;
      const spaceBelow = window.innerHeight - rect.bottom - GAP;
      const maxHeight = Math.min(Math.max(spaceBelow, 80), 200);

      setDropdownPos({ top: rect.bottom + GAP, left: rect.left, width: rect.width, maxHeight });
    }
  }, [isOpen]);

  const selectedOption = options.find((o) => o.id === value);
  // options에 아직 반영되지 않은 경우 pendingLabel을 fallback으로 사용
  const displayText = selectedOption ? selectedOption.name : (pendingLabel ?? '');

  // options에 해당 항목이 나타나면 pendingLabel 해제
  useEffect(() => {
    if (pendingLabel !== null && selectedOption) {
      setPendingLabel(null);
    }
  }, [pendingLabel, selectedOption]);

  const trimmed = search.trim();
  const filtered = useMemo(() =>
    options.filter((o) => !search || o.name.toLowerCase().includes(search.toLowerCase())),
    [options, search]
  );
  const exactMatch = options.some((o) => o.name === trimmed);
  const canAddNew = !!onAdd && !!trimmed;
  const clearRowLabel = value ? placeholder.replace(/선택$/, '선택 해제') : placeholder;

  // 외부 클릭 시 닫기 (portal 드롭다운 내부 클릭은 닫지 않음, isOpen일 때만 등록)
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOpen = () => {
    if (disabled) return;
    if (sheetMode) {
      setSearch('');
      setSheetOpen(true);
      return;
    }
    if (isOpen) {
      setIsOpen(false);
      setSearch('');
      return;
    }
    setSearch('');
    setIsOpen(true);
    requestAnimationFrame(() => { inputRef.current?.focus(); inputRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); });
  };

  // 시트 열릴 때 배경 스크롤 잠금
  useEffect(() => {
    if (sheetOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [sheetOpen]);

  const closeSheet = () => {
    setSheetOpen(false);
    setSearch('');
  };

  const handleSelect = (id: string) => {
    onChange(id);
    if (sheetMode) {
      closeSheet();
    } else {
      setIsOpen(false);
      setSearch('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const handleAdd = async () => {
    if (!onAdd || !trimmed || isAdding) return;
    setIsAdding(true);
    try {
      const newId = await onAdd(trimmed);
      setPendingLabel(trimmed); // options 업데이트 전 표시용 fallback
      onChange(newId);
      if (sheetMode) {
        closeSheet();
      } else {
        setIsOpen(false);
        setSearch('');
      }
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div ref={containerRef} className="flex-1 min-w-0 relative">
      <label className="block text-[10px] font-medium text-ink-400 mb-0.5">{label}</label>
      {/* 트리거 */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className={[
          'w-full flex items-center gap-1 px-2 py-1.5 text-xs border rounded-control text-left',
          'focus:outline-none focus:ring-1 focus:ring-brand-500',
          disabled
            ? 'bg-surface-soft border-line text-ink-400 cursor-not-allowed'
            : 'bg-white border-line text-ink-700 hover:border-brand-400 cursor-pointer',
        ].join(' ')}
      >
        <span className="flex-1 truncate min-w-0">
          {displayText || <span className="text-ink-400">{placeholder}</span>}
        </span>
        {value && !disabled && (
          <span
            role="button"
            aria-label="선택 해제"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleClear(e as unknown as React.MouseEvent);
            }}
            className="flex-shrink-0 text-ink-400 hover:text-ink-600 cursor-pointer"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        )}
        <svg
          className={`flex-shrink-0 w-3 h-3 text-ink-400 transition-transform ${(sheetMode ? sheetOpen : isOpen) ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {/* 시트 모드 (createPortal로 body에 렌더링) */}
      {sheetMode && sheetOpen && createPortal(
        <>
          {/* 배경 오버레이 */}
          <div className="fixed inset-0 z-[9998] bg-black/30" onClick={closeSheet} style={{ touchAction: 'none' }} />
          {/* 시트 */}
          <div
            className="fixed left-0 right-0 z-[9999] bg-white rounded-t-2xl shadow-xl flex flex-col"
            style={{ bottom: sheetStyle.bottom ?? 0, maxHeight: sheetStyle.maxHeight ?? '70vh' }}
          >
            {/* 핸들 */}
            <div className="flex justify-center pt-2 pb-0.5">
              <div className="w-10 h-1 bg-ink-300 rounded-full" />
            </div>
            {/* 헤더 */}
            <div className="flex items-center justify-between px-4 py-1.5 border-b border-line-soft">
              <h2 className="text-base font-semibold text-ink-900">{label || '선택'}</h2>
              <button
                type="button"
                onClick={closeSheet}
                className="p-1 text-ink-400 hover:text-ink-600"
                aria-label="닫기"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* 검색 */}
            <div className="px-4 pt-2 pb-1.5">
              <input
                ref={sheetInputRef}
                type="text"
                enterKeyHint="done"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (canAddNew) {
                      handleAdd();
                    } else {
                      (e.target as HTMLInputElement).blur();
                    }
                  } else if (e.key === 'Escape') {
                    closeSheet();
                  }
                }}
                placeholder="검색..."
                className="w-full px-3 py-2 text-sm border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            {/* 옵션 목록 */}
            <ul className="flex-1 overflow-y-auto px-4 pb-4" style={{ overscrollBehavior: 'contain' }}>
              {/* 선택 안함 */}
              <li>
                <button
                  type="button"
                  onClick={() => handleSelect('')}
                  className="w-full px-3 py-2.5 text-sm text-ink-400 hover:bg-surface-hover text-left rounded-card"
                >
                  {clearRowLabel}
                </button>
              </li>
              {filtered.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(o.id)}
                    className={[
                      'w-full px-3 py-2.5 text-sm text-left rounded-card flex items-center justify-between',
                      value === o.id ? 'text-brand-600 font-medium bg-brand-50' : 'text-ink-700 hover:bg-surface-hover',
                    ].join(' ')}
                  >
                    <span>{o.name}</span>
                    {value === o.id && (
                      <svg className="w-4 h-4 text-brand-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                </li>
              ))}
              {filtered.length === 0 && trimmed && !canAddNew && (
                <li className="px-3 py-4 text-sm text-ink-400 text-center">
                  검색 결과 없음
                </li>
              )}
              {canAddNew && (
                <li className="pt-2">
                  <button
                    type="button"
                    disabled={isAdding}
                    onClick={handleAdd}
                    className="w-full px-3 py-2.5 text-sm text-brand-600 font-medium hover:bg-brand-50 rounded-card text-left disabled:opacity-50"
                  >
                    {isAdding ? '추가 중...' : `"${trimmed}" 추가`}
                  </button>
                </li>
              )}
            </ul>
          </div>
        </>,
        document.body,
      )}

      {/* 드롭다운 (createPortal로 body에 렌더링) */}
      {!sheetMode && isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
          className="bg-white border border-line rounded-card shadow-card z-[9999] min-w-0"
        >
          {/* 검색 입력 */}
          <div className="p-1.5 border-b border-line-soft flex items-center gap-1">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && canAddNew) {
                  e.preventDefault();
                  handleAdd();
                } else if (e.key === 'Escape') {
                  setIsOpen(false);
                  setSearch('');
                }
              }}
              placeholder="검색..."
              className="flex-1 px-1.5 py-1 text-xs border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 min-w-0"
            />
            {canAddNew && (
              <Button
                type="button"
                variant="primary"
                size="xs"
                className="flex-shrink-0 whitespace-nowrap px-1.5 text-[10px]"
                disabled={isAdding}
                onMouseDown={(e) => { e.preventDefault(); handleAdd(); }}
              >
                {isAdding ? '추가 중...' : (exactMatch ? '+ 새로 등록' : '+ 추가')}
              </Button>
            )}
          </div>
          {/* 옵션 목록 */}
          <ul style={{ maxHeight: `${dropdownPos.maxHeight - SEARCH_BAR_HEIGHT}px` }} className="overflow-y-auto py-0.5">
            <li>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(''); }}
                className="w-full px-2.5 py-1.5 text-xs text-ink-400 hover:bg-surface-hover text-left"
              >
                {clearRowLabel}
              </button>
            </li>
            {filtered.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(o.id); }}
                  className={[
                    'w-full px-2.5 py-1.5 text-xs text-left hover:bg-surface-hover',
                    value === o.id ? 'text-brand-600 font-medium bg-brand-50' : 'text-ink-700',
                  ].join(' ')}
                >
                  {o.name}
                </button>
              </li>
            ))}
            {filtered.length === 0 && trimmed && !canAddNew && (
              <li className="px-2.5 py-2 text-xs text-ink-400 text-center">
                검색 결과 없음
              </li>
            )}
            {filtered.length === 0 && trimmed && canAddNew && (
              <li className="px-2.5 py-2 text-xs text-ink-400 text-center">
                "{trimmed}" — 추가 버튼으로 등록
              </li>
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
